import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { format, isValid, parseISO, subMonths } from 'date-fns';
import { FilterListComponent, ListItemRendererComponent } from 'gung-list';
import { Subject, takeUntil } from 'rxjs';
import { Order } from '../../models/order';
import { OpenOrdersConfigService } from '../../services/open-orders-config/open-orders-config.service';

@Component({
  selector: 'lib-open-orders',
  template: `
    <h3 translate class="page-title text-center">OPEN_ORDERS</h3>
    <div *ngIf="dateWindowEnabled" class="d-flex justify-content-between align-items-center mb-3">
      <div *ngIf="showEmptyFilteredSuggestion; else noSuggestion">
        <span translate>NO_ORDERS_FOUND_TRY_EXTENDING_DATE_RANGE</span>
        <button class="btn btn-primary ms-1 p-1" (click)="loadSixMonthsBack()" translate>LOAD_6_MONTHS_BACK</button>
      </div>
      <ng-template #noSuggestion><div></div></ng-template>
      <div class="d-flex align-items-center">
        <i *ngIf="loading" class="fa fa-spinner fa-spin me-2"></i>
        <i *ngIf="showCheckmark" class="fa fa-check text-success me-2"></i>
        <label class="me-2 mb-0" translate>SHOW_ORDERS_FROM</label>
        <input type="date" class="form-control" style="width: auto;" [max]="maxSelectableDate" [(ngModel)]="minDate" />
        <button class="btn btn-primary ms-2" (click)="applyMinDate()" [disabled]="loading || !minDate" translate>
          APPLY
        </button>
      </div>
    </div>
    <lib-filter-list #filterList [configService]="config" (loaded)="onLoaded($event)"></lib-filter-list>
  `,
  styleUrls: ['./open-orders.component.css'],
  standalone: false
})
export class OpenOrdersComponent
  extends ListItemRendererComponent<Order[]>
  implements OnInit, AfterViewInit, OnDestroy
{
  public config = inject(OpenOrdersConfigService);

  @ViewChild('filterList') filterList: FilterListComponent<Order>;

  public dateWindowEnabled = false;
  public minDate: string;
  public maxSelectableDate = format(new Date(), 'yyyy-MM-dd');
  public loading = false;
  public showCheckmark = false;
  public showEmptyFilteredSuggestion = false;

  private hasLoadedOnce = false;
  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.minDate = this.config.getMinDate?.();
    this.dateWindowEnabled = !!this.minDate;
  }

  ngAfterViewInit(): void {
    if (this.dateWindowEnabled) {
      // renderItems is a BehaviorSubject seeded with [] and also emits empty before the first
      // fetch resolves (layout sizing). Only judge emptiness once data has actually arrived,
      // otherwise the suggestion shows during the initial load.
      this.filterList.renderItems.pipe(takeUntil(this.destroy$)).subscribe(renderedItems => {
        this.showEmptyFilteredSuggestion = this.hasLoadedOnce && !renderedItems?.length;
      });
    }
    this.renderFinished?.emit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  applyMinDate(): void {
    this.loading = true;
    this.showCheckmark = false;
    this.showEmptyFilteredSuggestion = false;
    this.config.setMinDate(this.minDate);
  }

  loadSixMonthsBack(): void {
    // The date input can be cleared by the user, so fall back to today rather than parsing ''.
    const current = parseISO(this.minDate ?? '');
    this.minDate = format(subMonths(isValid(current) ? current : new Date(), 6), 'yyyy-MM-dd');
    this.applyMinDate();
  }

  onLoaded(loaded: boolean): void {
    if (loaded) {
      this.hasLoadedOnce = true;
    }
    if (loaded && this.loading) {
      this.loading = false;
      this.showCheckmark = true;
      setTimeout(() => {
        this.showCheckmark = false;
      }, 2000);
    }
  }
}
